import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  DashboardData,
  DashboardSection,
} from '../types/dashboard';
import {
  getDashboardData,
  getLeaveBalanceSummary,
  getMonthOverviewSummary,
  getPendingApprovalSummary,
  getTodayAttendance,
  getUnreadNotificationSummary,
  getUpcomingHolidaySummary,
  getWeeklyAttendanceSummary,
  normalizeDashboardSectionError,
} from '../services/dashboardService';

export function useDashboard(permissions: readonly string[], userId?: number) {
  const [data, setData] = useState<DashboardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date | null>(null);
  const mounted = useRef(true);
  const generation = useRef(0);
  const permissionKey = permissions.join('|');

  const run = useCallback(
    async (refreshing = false) => {
      // A refresh that starts while another load is in flight must win.
      // Dropping it left web break/check-in showing the pre-punch snapshot.
      const id = ++generation.current;
      refreshing ? setIsRefreshing(true) : setIsLoading(true);
      setError(null);
      try {
        const next = await getDashboardData(permissions);
        if (!mounted.current || id !== generation.current) return;
        setData(next);
        setLastUpdatedAt(new Date());
        const visibleSections = [
          ...(next.attendance || next.errors.attendance ? ['attendance'] : []),
          ...(next.approvals.visible || next.errors.approvals ? ['approvals'] : []),
          ...(next.leaveBalance.visible || next.errors.leaveBalance
            ? ['leaveBalance']
            : []),
          ...(next.monthOverview.visible || next.errors.monthOverview
            ? ['monthOverview']
            : []),
          ...(next.weeklyAttendance.visible || next.errors.weeklyAttendance
            ? ['weeklyAttendance']
            : []),
          'upcomingHolidays',
          'notifications',
        ] as DashboardSection[];
        if (visibleSections.every(section => next.errors[section])) {
          setError('Unable to connect to the server.');
        }
      } catch {
        if (!mounted.current || id !== generation.current) return;
        setError('Unable to connect to the server.');
      } finally {
        if (mounted.current && id === generation.current) {
          setIsLoading(false);
          setIsRefreshing(false);
        }
      }
    },
    // A stable string intentionally controls permission-driven reloads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [permissionKey, userId],
  );

  useEffect(() => {
    mounted.current = true;
    run();
    return () => {
      mounted.current = false;
    };
  }, [run]);

  const retrySection = useCallback(
    async (section: DashboardSection) => {
      try {
        const result =
          section === 'attendance'
            ? await getTodayAttendance()
            : section === 'approvals'
              ? await getPendingApprovalSummary(permissions)
              : section === 'leaveBalance'
                ? await getLeaveBalanceSummary(permissions)
                : section === 'monthOverview'
                  ? await getMonthOverviewSummary(permissions)
                  : section === 'weeklyAttendance'
                    ? await getWeeklyAttendanceSummary(permissions)
                    : section === 'upcomingHolidays'
                      ? await getUpcomingHolidaySummary()
                      : await getUnreadNotificationSummary();
        if (!mounted.current) return;
        setData(current =>
          current
            ? {
                ...current,
                [section]: result,
                errors: { ...current.errors, [section]: undefined },
              }
            : current,
        );
        setLastUpdatedAt(new Date());
      } catch (caught) {
        if (!mounted.current) return;
        setData(current =>
          current
            ? {
                ...current,
                errors: {
                  ...current.errors,
                  [section]: normalizeDashboardSectionError(caught),
                },
              }
            : current,
        );
      }
    },
    // See permissionKey above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [permissionKey],
  );

  return {
    data,
    isLoading,
    isRefreshing,
    error,
    sectionErrors: data?.errors ?? {},
    lastUpdatedAt,
    loadDashboard: () => run(false),
    refreshDashboard: () => run(true),
    retrySection,
  };
}
